/**
 * WeatherStatusBadge — project/cycle health as weather conditions.
 * Expeditions are governed by conditions; this maps status to terrain-weather metaphors.
 *
 * Usage:
 *   <WeatherStatusBadge status="on_track" />
 *   <WeatherStatusBadge status="blocked" showLabel={false} />
 */
import { Sun, CloudRain, Zap, Map, Flag, Cloud } from 'lucide-react';

export type WeatherStatus =
  | 'on_track'
  | 'at_risk'
  | 'blocked'
  | 'planning'
  | 'complete'
  | 'paused';

interface WeatherConfig {
  icon: React.ElementType;
  label: string;
  badgeClass: string;
  iconClass: string;
}

const WEATHER_CONFIG: Record<WeatherStatus, WeatherConfig> = {
  on_track: {
    icon: Sun,
    label: 'On Track',
    badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]',
    iconClass: 'text-[var(--mustard)]',
  },
  at_risk: {
    icon: CloudRain,
    label: 'At Risk',
    badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
    iconClass: 'text-[var(--text-ter)]',
  },
  blocked: {
    icon: Zap,
    label: 'Blocked',
    badgeClass: 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]',
    iconClass: 'text-[var(--coral)]',
  },
  planning: {
    icon: Map,
    label: 'Planning',
    badgeClass: 'bg-[var(--surface)] text-[var(--text-sec)] border border-[var(--rule)]',
    iconClass: 'text-[var(--text-ter)]',
  },
  complete: {
    icon: Flag,
    label: 'Complete',
    badgeClass: 'bg-primary-50 text-primary-700 border border-primary-200',
    iconClass: 'text-primary-600',
  },
  paused: {
    icon: Cloud,
    label: 'Paused',
    badgeClass: 'bg-[var(--surface)] text-[var(--text-ter)] border border-[var(--rule)]',
    iconClass: 'text-[var(--text-ter)]',
  },
};

interface WeatherStatusBadgeProps {
  status: WeatherStatus;
  showLabel?: boolean;
  className?: string;
}

export function WeatherStatusBadge({
  status,
  showLabel = true,
  className = '',
}: WeatherStatusBadgeProps) {
  const config = WEATHER_CONFIG[status];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.badgeClass} ${className}`}
      title={config.label}
    >
      <Icon className={`h-3 w-3 flex-shrink-0 ${config.iconClass}`} aria-hidden="true" />
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}
